import * as React from 'react'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { buildSavedAttackTemplatesUrl } from './attackTemplatePlatform'
import CompactAssetIcon from './CompactAssetIcon'
import { AQUA_SHOP_ICON_BY_KEY } from './gameAssetIcons'
import UnitIconFrame, { resolveUnitLevelBadge } from './UnitIconFrame'

const STORM_PLUGIN_KEY = 'attackforts'
const STORM_SETTINGS_KEY = 'stormBotSettings'
const CARGO_PLUGIN_KEY = 'islandcargo'
const CARGO_SETTINGS_KEY = 'stormCargoSettings'
const TROOP_PICKER_FAVORITES_KEY = 'stormBotTroopPickerFavorites'
const CARGO_LEVEL_OPTIONS = Object.freeze([2, 3, 4, 5, 6, 7, 8, 9, 10])
const CARGO_SKIP_OPTIONS = Object.freeze([
    { key: 'MS7', label: '24h' },
    { key: 'MS6', label: '5h' },
    { key: 'MS5', label: '1h' },
    { key: 'MS4', label: '30m' },
    { key: 'MS3', label: '10m' },
    { key: 'MS2', label: '5m' },
    { key: 'MS1', label: '1m' }
])
const EXPANSION_MODE_OPTIONS = Object.freeze([
    { value: 'disabled', label: 'Disabled' },
    { value: 'wood_stone', label: 'Wood & Stone' }
])
const SIEGE_WORKSHOP_SLOT_COUNT = 5
const SIEGE_WORKSHOP_TOOL_OPTIONS = Object.freeze([
    { value: '', label: 'Disabled' },
    { value: 'ladder', label: 'Scaling ladder' },
    { value: 'mantlet', label: 'Mantlet' }
])

const TARGET_RULE_DEFAULTS = Object.freeze([
    { key: '80_EMPTY', level: 80, setup: 'EMPTY', active: true },
    { key: '80_FULL', level: 80, setup: 'FULL', active: true },
    { key: '70_EMPTY', level: 70, setup: 'EMPTY', active: true },
    { key: '70_FULL', level: 70, setup: 'FULL', active: true },
    { key: '60_EMPTY', level: 60, setup: 'EMPTY', active: true },
    { key: '60_FULL', level: 60, setup: 'FULL', active: true },
    { key: '50', level: 50, setup: 'EMPTY', active: true },
    { key: '40', level: 40, setup: 'EMPTY', active: true }
])

const SOURCE_CASTLE_OPTIONS = Object.freeze([
    { value: 'main', label: 'Main' },
    { value: 'ice', label: 'Ice' },
    { value: 'desert', label: 'Desert' },
    { value: 'fire', label: 'Fire' },
    { value: 'capital', label: 'Capital' },
    { value: 'metro', label: 'Metro' }
])

const SKIP_TIME_OPTIONS = Object.freeze([
    { value: '1', label: '1 Minute' },
    { value: '5', label: '5 Minutes' },
    { value: '10', label: '10 Minutes' },
    { value: '30', label: '30 Minutes' },
    { value: '60', label: '60 Minutes' },
    { value: '300', label: '5 Hours' },
    { value: '1440', label: '24 Hours' }
])

const DEFAULT_TROOP_REFERENCE = Object.freeze({
    id: '215',
    label: '215: Shield-maiden',
    itemType: 'troop',
    statLine: 'Troop',
    level: '10'
})

const DEFAULT_STORM_BOT_CONFIG = Object.freeze({
    attackCountThreshold: '1800',
    useFeathers: true,
    useVipCommanders: true,
    scanIntervalHours: '0.5',
    minimumWinsRemaining: '2',
    targetRules: TARGET_RULE_DEFAULTS,
    automaticSetup: {
        supportWaveAmount: '300',
        troop: DEFAULT_TROOP_REFERENCE,
        wave34TroopCount: '1'
    },
    playerGeneratedTemplates: {
        enabled: false,
        emptyTemplate: {
            templateId: '',
            name: '',
            ownerUsername: '',
            description: '',
            template: null
        },
        fullTemplate: {
            templateId: '',
            name: '',
            ownerUsername: '',
            description: '',
            template: null
        }
    },
    toolSettings: {
        minimumLadders: '1000',
        minimumRams: '1000'
    },
    siegeWorkshop: {
        enabled: false,
        intervalSeconds: '60',
        slots: [
            { tool: 'ladder', amount: '80' },
            { tool: 'mantlet', amount: '80' },
            { tool: '', amount: '80' },
            { tool: '', amount: '80' },
            { tool: '', amount: '80' }
        ]
    },
    aquaShop: {
        enabled: true,
        buyWarHorn: false,
        buyTurtleRam: false,
        buyGrapplingHooks: false,
        buyDummyPeasants: false,
        buyCoins: false,
        buyCrabRock: false,
        buyWhaleLagoon: false,
        buyTideGarden: false,
        buySilverPieces: false,
        buyGoldPieces: false,
        buyGemCommander: false,
        buyGemCastellan: false,
        buy5hSkips: false,
        buy24hSkips: false
    },
    foodSupply: {
        enabled: false,
        sourceCastle: 'main',
        minimumStorm: '20000',
        targetStorm: '80000',
        keepMinimumSource: '100000',
        minimumSendAmount: '10000',
        timeSkipType: '30',
        timeSkipAmount: '2'
    },
    meadSupply: {
        enabled: true,
        sourceCastle: 'main',
        minimumStorm: '20000',
        targetStorm: '100000',
        keepMinimumSource: '100000',
        minimumSendAmount: '10000',
        timeSkipType: '30',
        timeSkipAmount: '2'
    },
    troopSupply: {
        enabled: true,
        sourceCastle: 'main',
        troop: DEFAULT_TROOP_REFERENCE,
        minimumStorm: '1000',
        targetStorm: '2000',
        keepMinimumSource: '0',
        minimumSendAmount: '500',
        requireFoodMead: true,
        timeSkipType: '30',
        timeSkipAmount: '2'
    }
})

const DEFAULT_STORM_CARGO_CONFIG = Object.freeze({
    shipBuildingEnabled: true,
    buildMaxShips: true,
    targetTotalShips: '0',
    upgradeLevels: Object.freeze(
        Object.fromEntries(CARGO_LEVEL_OPTIONS.map(level => [level, true]))
    ),
    autoUpgradeStorehouse: false,
    expansionMode: 'wood_stone',
    useMinuteSkips: true,
    allowedSkipTypes: Object.freeze(
        Object.fromEntries(CARGO_SKIP_OPTIONS.map(option => [option.key, true]))
    )
})

const AQUA_SHOP_FIELDS = Object.freeze([
    { key: 'enabled', label: 'Enable Aqua Shop', helper: 'Enable automatic Aqua shop purchases' },
    { key: 'buyCrabRock', label: 'Aqua: Buy Crab rock', helper: 'Buy Crab rock for 75,000 aquamarine' },
    { key: 'buyWhaleLagoon', label: 'Aqua: Buy Whale lagoon', helper: 'Buy Whale lagoon for 100,000 aquamarine' },
    { key: 'buyTideGarden', label: 'Aqua: Buy Tide garden', helper: 'Buy Tide garden for 75,000 aquamarine' },
    { key: 'buySilverPieces', label: 'Aqua: Buy Silver pieces', helper: 'Buy 1,000 silver pieces for 10,000 aquamarine' },
    { key: 'buyGoldPieces', label: 'Aqua: Buy Gold pieces', helper: 'Buy 500 gold pieces for 10,000 aquamarine' },
    { key: 'buy5hSkips', label: 'Aqua: Buy Skip 5 hours', helper: 'Buy 50 five-hour skips for 40,000 aquamarine' },
    { key: 'buy24hSkips', label: 'Aqua: Buy Skip 24 hours', helper: 'Buy 10 twenty-four-hour skips for 40,000 aquamarine' },
    { key: 'buyGemCastellan', label: 'Aqua: Buy Undefined gem (Castellan)', helper: 'Buy undefined gem package for 50,000 aquamarine' },
    { key: 'buyGemCommander', label: 'Aqua: Buy Undefined gem (Commander)', helper: 'Buy undefined gem package for 50,000 aquamarine' },
    { key: 'buyWarHorn', label: 'Aqua: Buy War horn', helper: 'Buy 5 War horns for 2,960 aquamarine every check while selected' },
    { key: 'buyTurtleRam', label: 'Aqua: Buy Turtle ram', helper: 'Buy 5 Turtle rams for 1,530 aquamarine every check while selected' },
    { key: 'buyGrapplingHooks', label: 'Aqua: Buy Grappling hooks', helper: 'Buy 5 Grappling hooks for 1,530 aquamarine every check while selected' },
    { key: 'buyDummyPeasants', label: 'Aqua: Buy Dummy peasants', helper: 'Buy 5 Dummy peasants for 1,570 aquamarine every check while selected' },
    { key: 'buyCoins', label: 'Aqua: Buy Coins', helper: 'Buy coin packages from Aqua shop in game order: 11M, 7.5M, 5M, then 2M' }
])

const AQUA_SHOP_TOOL_KEYS = new Set(['buyWarHorn', 'buyTurtleRam', 'buyGrapplingHooks', 'buyDummyPeasants'])
const AQUA_SHOP_BUILDING_KEYS = new Set(['buyCrabRock', 'buyWhaleLagoon', 'buyTideGarden'])

const getAquaShopIconType = key =>
    AQUA_SHOP_TOOL_KEYS.has(key) ? 'tool' :
        AQUA_SHOP_BUILDING_KEYS.has(key) ? 'building' :
            'resource'

const PRIMARY_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: 'rgba(23, 34, 55, 0.72)'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.01rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

const SECTION_SX = {
    borderRadius: '22px',
    border: '1px solid rgba(129, 146, 178, 0.18)',
    background: 'linear-gradient(180deg, rgba(21, 33, 55, 0.62) 0%, rgba(18, 29, 48, 0.5) 100%)',
    boxShadow: '0 14px 32px rgba(7, 13, 26, 0.18)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(180deg, rgba(92, 118, 163, 0.07) 0%, rgba(92, 118, 163, 0) 22%)',
        pointerEvents: 'none'
    }
}

const SUBSECTION_SX = {
    borderRadius: '18px',
    border: '1px solid rgba(129, 146, 178, 0.14)',
    backgroundColor: 'rgba(16, 25, 41, 0.44)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.02)'
}

const TARGET_RULE_CARD_SX = {
    borderRadius: '16px',
    border: '1px solid rgba(129, 146, 178, 0.16)',
    backgroundColor: 'rgba(24, 36, 60, 0.64)'
}

const cloneValue = value => {
    if (typeof structuredClone === 'function')
        return structuredClone(value)

    return JSON.parse(JSON.stringify(value ?? {}))
}

const toStringValue = (value, fallback = '') =>
    [undefined, null].includes(value) ? fallback : String(value)

const normalizeSearchText = value =>
    String(value ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

const normalizeWholeNumber = value => {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? Math.floor(numericValue) : undefined
}

const formatRuntimeDateTime = value => {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue) || numericValue <= 0)
        return 'Waiting for sync'

    return new Date(numericValue).toLocaleString([], {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const extractLevelValue = (statLine, fallbackLevel = '') => {
    const match = String(statLine ?? '').match(/Level\s+([0-9]+)/i)
    return (match ? match[1] : '') || toStringValue(fallbackLevel)
}

const normalizeTroopReference = value => ({
    id: toStringValue(value?.id ?? value?.value),
    label: toStringValue(value?.label),
    itemType: value?.itemType === 'tool' ? 'tool' : 'troop',
    statLine: toStringValue(value?.statLine, value?.itemType === 'tool' ? 'Tool' : 'Troop'),
    level: toStringValue(value?.level, extractLevelValue(value?.statLine)),
    iconUrl: toStringValue(value?.iconUrl),
    assetKey: toStringValue(value?.assetKey)
})

const normalizeSavedTemplateReference = value => ({
    templateId: [undefined, null, ''].includes(value?.templateId) ? '' : String(value.templateId),
    name: toStringValue(value?.name),
    ownerUsername: toStringValue(value?.ownerUsername),
    description: toStringValue(value?.description),
    template: value?.template && typeof value.template === 'object' ? cloneValue(value.template) : null
})

const normalizeTargetRule = (value, index) => {
    const fallback = TARGET_RULE_DEFAULTS[index] ?? TARGET_RULE_DEFAULTS[TARGET_RULE_DEFAULTS.length - 1]
    const key = String(value?.key ?? fallback?.key ?? `Rule ${index + 1}`)
    const numericLevel = Number(value?.level ?? key.split('_')[0] ?? fallback?.level)

    return {
        key,
        level: Number.isFinite(numericLevel) ? numericLevel : Number(fallback?.level ?? 0),
        setup: String(value?.setup ?? fallback?.setup ?? 'EMPTY').toUpperCase() === 'FULL' ? 'FULL' : 'EMPTY',
        active: value?.active !== false
    }
}

const normalizeSupplySection = (value, defaults, allowTroop = false) => ({
    enabled: Boolean(value?.enabled ?? defaults.enabled),
    sourceCastle: toStringValue(value?.sourceCastle, defaults.sourceCastle),
    minimumStorm: toStringValue(value?.minimumStorm, defaults.minimumStorm),
    targetStorm: toStringValue(value?.targetStorm, defaults.targetStorm),
    keepMinimumSource: toStringValue(value?.keepMinimumSource, defaults.keepMinimumSource),
    minimumSendAmount: toStringValue(value?.minimumSendAmount, defaults.minimumSendAmount),
    timeSkipType: toStringValue(value?.timeSkipType, defaults.timeSkipType),
    timeSkipAmount: toStringValue(value?.timeSkipAmount, defaults.timeSkipAmount),
    troop: allowTroop ? normalizeTroopReference(value?.troop ?? defaults.troop) : undefined,
    requireFoodMead: allowTroop ? Boolean(value?.requireFoodMead ?? defaults.requireFoodMead) : undefined
})

const normalizeSiegeWorkshopSlot = (value, index) => {
    const fallback = DEFAULT_STORM_BOT_CONFIG.siegeWorkshop.slots[index] ?? { tool: '', amount: '80' }
    const tool = SIEGE_WORKSHOP_TOOL_OPTIONS.some(option => option.value === String(value?.tool ?? fallback.tool))
        ? String(value?.tool ?? fallback.tool)
        : ''

    return {
        tool,
        amount: toStringValue(value?.amount, fallback.amount)
    }
}

const normalizeSiegeWorkshopConfig = value => ({
    enabled: Boolean(value?.enabled ?? DEFAULT_STORM_BOT_CONFIG.siegeWorkshop.enabled),
    intervalSeconds: toStringValue(value?.intervalSeconds, DEFAULT_STORM_BOT_CONFIG.siegeWorkshop.intervalSeconds),
    slots: Array.from({ length: SIEGE_WORKSHOP_SLOT_COUNT }, (_, index) =>
        normalizeSiegeWorkshopSlot(value?.slots?.[index], index))
})

const normalizeStormBotConfig = pluginState => {
    const source = pluginState?.[STORM_SETTINGS_KEY] && typeof pluginState[STORM_SETTINGS_KEY] === 'object' ?
        pluginState[STORM_SETTINGS_KEY] :
        {}

    return {
        attackCountThreshold: toStringValue(source.attackCountThreshold, DEFAULT_STORM_BOT_CONFIG.attackCountThreshold),
        useFeathers: Boolean(source.useFeathers ?? pluginState?.useLeather ?? DEFAULT_STORM_BOT_CONFIG.useFeathers),
        useVipCommanders: Boolean(source.useVipCommanders ?? pluginState?.useVipCommanders ?? DEFAULT_STORM_BOT_CONFIG.useVipCommanders),
        scanIntervalHours: toStringValue(source.scanIntervalHours, DEFAULT_STORM_BOT_CONFIG.scanIntervalHours),
        minimumWinsRemaining: toStringValue(source.minimumWinsRemaining, DEFAULT_STORM_BOT_CONFIG.minimumWinsRemaining),
        targetRules: Array.isArray(source.targetRules) && source.targetRules.length > 0 ?
            source.targetRules.map(normalizeTargetRule) :
            TARGET_RULE_DEFAULTS.map(normalizeTargetRule),
        automaticSetup: {
            supportWaveAmount: toStringValue(source?.automaticSetup?.supportWaveAmount, DEFAULT_STORM_BOT_CONFIG.automaticSetup.supportWaveAmount),
            troop: normalizeTroopReference(source?.automaticSetup?.troop ?? DEFAULT_STORM_BOT_CONFIG.automaticSetup.troop),
            wave34TroopCount: toStringValue(source?.automaticSetup?.wave34TroopCount, DEFAULT_STORM_BOT_CONFIG.automaticSetup.wave34TroopCount)
        },
        playerGeneratedTemplates: {
            enabled: Boolean(source?.playerGeneratedTemplates?.enabled),
            emptyTemplate: normalizeSavedTemplateReference(source?.playerGeneratedTemplates?.emptyTemplate),
            fullTemplate: normalizeSavedTemplateReference(source?.playerGeneratedTemplates?.fullTemplate)
        },
        toolSettings: {
            minimumLadders: toStringValue(source?.toolSettings?.minimumLadders, DEFAULT_STORM_BOT_CONFIG.toolSettings.minimumLadders),
            minimumRams: toStringValue(source?.toolSettings?.minimumRams, DEFAULT_STORM_BOT_CONFIG.toolSettings.minimumRams)
        },
        siegeWorkshop: normalizeSiegeWorkshopConfig(source?.siegeWorkshop),
        aquaShop: {
            enabled: Boolean(source?.aquaShop?.enabled ?? DEFAULT_STORM_BOT_CONFIG.aquaShop.enabled),
            buyWarHorn: Boolean(source?.aquaShop?.buyWarHorn ?? source?.aquaShop?.buyFront),
            buyTurtleRam: Boolean(source?.aquaShop?.buyTurtleRam),
            buyGrapplingHooks: Boolean(source?.aquaShop?.buyGrapplingHooks ?? source?.aquaShop?.buyWall),
            buyDummyPeasants: Boolean(source?.aquaShop?.buyDummyPeasants ?? pluginState?.buyFalsePeasants),
            buyCoins: Boolean(source?.aquaShop?.buyCoins ?? pluginState?.buycoins),
            buyCrabRock: Boolean(source?.aquaShop?.buyCrabRock),
            buyWhaleLagoon: Boolean(source?.aquaShop?.buyWhaleLagoon ?? pluginState?.buydeco),
            buyTideGarden: Boolean(source?.aquaShop?.buyTideGarden),
            buySilverPieces: Boolean(source?.aquaShop?.buySilverPieces),
            buyGoldPieces: Boolean(source?.aquaShop?.buyGoldPieces),
            buyGemCommander: Boolean(source?.aquaShop?.buyGemCommander),
            buyGemCastellan: Boolean(source?.aquaShop?.buyGemCastellan),
            buy5hSkips: Boolean(source?.aquaShop?.buy5hSkips ?? pluginState?.buy5hSkip),
            buy24hSkips: Boolean(source?.aquaShop?.buy24hSkips ?? pluginState?.buy24hSkip)
        },
        foodSupply: normalizeSupplySection(source?.foodSupply, DEFAULT_STORM_BOT_CONFIG.foodSupply),
        meadSupply: normalizeSupplySection(source?.meadSupply, DEFAULT_STORM_BOT_CONFIG.meadSupply),
        troopSupply: normalizeSupplySection(source?.troopSupply, DEFAULT_STORM_BOT_CONFIG.troopSupply, true)
    }
}

const normalizeStormCargoConfig = pluginState => {
    const source = pluginState?.[CARGO_SETTINGS_KEY] && typeof pluginState[CARGO_SETTINGS_KEY] === 'object'
        ? pluginState[CARGO_SETTINGS_KEY]
        : {}

    const upgradeLevels = {}
    CARGO_LEVEL_OPTIONS.forEach(level => {
        upgradeLevels[level] = source?.upgradeLevels?.[level] === undefined
            ? DEFAULT_STORM_CARGO_CONFIG.upgradeLevels[level]
            : Boolean(source.upgradeLevels[level])
    })

    const allowedSkipTypes = {}
    const skipTypeSource = source?.allowedSkipTypes && typeof source.allowedSkipTypes === 'object'
        ? source.allowedSkipTypes
        : {}
    CARGO_SKIP_OPTIONS.forEach(option => {
        allowedSkipTypes[option.key] = skipTypeSource[option.key] === undefined
            ? DEFAULT_STORM_CARGO_CONFIG.allowedSkipTypes[option.key]
            : Boolean(skipTypeSource[option.key])
    })

    return {
        shipBuildingEnabled: Boolean(source.shipBuildingEnabled ?? source.enableShipBuilding ?? pluginState?.enabled ?? DEFAULT_STORM_CARGO_CONFIG.shipBuildingEnabled),
        buildMaxShips: Boolean(source.buildMaxShips ?? DEFAULT_STORM_CARGO_CONFIG.buildMaxShips),
        targetTotalShips: toStringValue(source.targetTotalShips, DEFAULT_STORM_CARGO_CONFIG.targetTotalShips),
        upgradeLevels,
        autoUpgradeStorehouse: Boolean(source.autoUpgradeStorehouse ?? DEFAULT_STORM_CARGO_CONFIG.autoUpgradeStorehouse),
        expansionMode: String(source.expansionMode) === 'rubies'
            ? 'disabled'
            : (EXPANSION_MODE_OPTIONS.some(option => option.value === String(source.expansionMode))
                ? String(source.expansionMode)
                : DEFAULT_STORM_CARGO_CONFIG.expansionMode),
        useMinuteSkips: Boolean(source.useMinuteSkips ?? pluginState?.useSkips ?? DEFAULT_STORM_CARGO_CONFIG.useMinuteSkips),
        allowedSkipTypes
    }
}

const loadTroopPickerFavorites = () => {
    if (typeof window === 'undefined')
        return []

    try {
        const parsed = JSON.parse(window.localStorage.getItem(TROOP_PICKER_FAVORITES_KEY) ?? '[]')
        return Array.isArray(parsed) ? parsed.map(value => String(value)).filter(Boolean) : []
    }
    catch {
        return []
    }
}

const saveTroopPickerFavorites = favoriteIds => {
    if (typeof window === 'undefined')
        return

    try {
        window.localStorage.setItem(TROOP_PICKER_FAVORITES_KEY, JSON.stringify(favoriteIds))
    }
    catch { }
}

const buildTroopCatalogEntries = catalog => {
    const entries = []
    const appendEntries = items => {
        for (const item of Array.isArray(items) ? items : []) {
            const id = toStringValue(item?.value ?? item?.id).trim()
            if (!id)
                continue

            entries.push({
                key: `troop:${id}`,
                id,
                label: toStringValue(item?.label, id),
                itemType: 'troop',
                statLine: toStringValue(item?.statLine, 'Troop'),
                level: extractLevelValue(item?.statLine, item?.level),
                iconUrl: toStringValue(item?.iconUrl),
                assetKey: toStringValue(item?.assetKey),
                searchText: toStringValue(item?.searchText, `${item?.label ?? ''} ${id} ${item?.statLine ?? ''}`)
            })
        }
    }

    appendEntries(catalog?.attackTroops)
    appendEntries(catalog?.defenseTroops)

    return entries.filter((entry, index, allEntries) =>
        allEntries.findIndex(otherEntry => otherEntry.id === entry.id) === index
    )
}

function HelperText({ children, sx }) {
    return (
        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.7, ...sx }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: '#95a5c4' }} />
            <Typography sx={{ fontSize: '0.89rem', color: '#95a5c4', lineHeight: 1.35 }}>
                {children}
            </Typography>
        </Stack>
    )
}

function SettingSwitch({ checked, onChange, disabled = false }) {
    return (
        <Switch
            checked={checked}
            disabled={disabled}
            onChange={event => onChange(event.target.checked)}
            sx={{
                width: 48,
                height: 29,
                p: 0,
                '& .MuiSwitch-switchBase': {
                    p: '2.5px',
                    '&.Mui-checked': {
                        transform: 'translateX(18px)',
                        color: '#ffffff'
                    },
                    '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#2f7cff',
                        opacity: 1
                    }
                },
                '& .MuiSwitch-thumb': {
                    width: 24,
                    height: 24,
                    boxShadow: 'none'
                },
                '& .MuiSwitch-track': {
                    opacity: 1,
                    borderRadius: '999px',
                    backgroundColor: 'rgba(111, 123, 143, 0.9)'
                }
            }}
        />
    )
}

function SectionHeading({ children }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1.3} sx={{ mb: 2.2 }}>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1.05rem', fontWeight: 800 }}>
                {children}
            </Typography>
            <Divider sx={{ flex: 1, borderColor: 'rgba(129, 146, 178, 0.22)' }} />
        </Stack>
    )
}

function ToggleField({ checked, onChange, label, helper, disabled = false, iconUrl = '', iconType = 'resource' }) {
    return (
        <Box sx={{ minHeight: iconUrl ? 74 : 54 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ pt: iconUrl ? 0.4 : 1.15 }}>
                {iconUrl ? (
                    <CompactAssetIcon
                        iconUrl={iconUrl}
                        itemType={iconType}
                        size={56}
                        sx={{ opacity: disabled ? 0.55 : 1 }}
                    />
                ) : null}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                        <SettingSwitch checked={checked} onChange={onChange} disabled={disabled} />
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, opacity: disabled ? 0.55 : 1, minWidth: 0 }}>
                            {label}
                        </Typography>
                    </Stack>
                    <HelperText sx={{ opacity: disabled ? 0.55 : 1 }}>{helper}</HelperText>
                </Box>
            </Stack>
        </Box>
    )
}

function InlineCheckboxOption({ checked, onChange, label, disabled = false }) {
    return (
        <Box
            onClick={() => {
                if (!disabled)
                    onChange(!checked)
            }}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.35,
                mr: 1.15,
                mb: 0.75,
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                userSelect: 'none'
            }}
        >
            <Checkbox
                checked={checked}
                disabled={disabled}
                onChange={event => onChange(event.target.checked)}
                size="small"
                sx={{
                    p: 0.25,
                    color: 'rgba(207, 219, 242, 0.72)',
                    '&.Mui-checked': {
                        color: '#3b82f6'
                    }
                }}
            />
            <Typography sx={{ color: '#f5f7ff', fontSize: '0.95rem', fontWeight: 700 }}>
                {label}
            </Typography>
        </Box>
    )
}

const hasSelectedCargoSkipType = cargoConfig =>
    CARGO_SKIP_OPTIONS.some(option => Boolean(cargoConfig?.allowedSkipTypes?.[option.key]))

export const shouldEnableStormBotPlugin = config =>
    Boolean(
        (config?.targetRules ?? []).some(rule => rule?.active !== false) ||
        config?.siegeWorkshop?.enabled ||
        config?.aquaShop?.enabled ||
        config?.foodSupply?.enabled ||
        config?.meadSupply?.enabled ||
        config?.troopSupply?.enabled
    )

export const hasExplicitStormRuntimeState = pluginConfig =>
    Boolean(
        pluginConfig &&
        typeof pluginConfig === 'object' &&
        Object.prototype.hasOwnProperty.call(pluginConfig, 'state')
    )

export const resolveStormBotRuntimeState = (pluginConfig, nextConfig) =>
    hasExplicitStormRuntimeState(pluginConfig) ?
        Boolean(pluginConfig?.state) :
        shouldEnableStormBotPlugin(nextConfig)

export const resolveStormCargoRuntimeState = ({
    stormPluginConfig,
    nextCargoConfig
}) => {
    const cargoAutomationEnabled = Boolean(
        nextCargoConfig?.shipBuildingEnabled ||
        nextCargoConfig?.autoUpgradeStorehouse
    )

    if (!hasExplicitStormRuntimeState(stormPluginConfig))
        return cargoAutomationEnabled

    return Boolean(stormPluginConfig?.state) && cargoAutomationEnabled
}

function TemplatePickerDialog({
    open,
    templates,
    loading,
    error,
    onClose,
    onReload,
    onSelect
}) {
    const [searchTerm, setSearchTerm] = React.useState('')
    const deferredSearchTerm = React.useDeferredValue(searchTerm)

    React.useEffect(() => {
        if (open)
            setSearchTerm('')
    }, [open])

    const filteredTemplates = React.useMemo(() => {
        const normalizedQuery = normalizeSearchText(deferredSearchTerm)

        if (!normalizedQuery)
            return templates

        return (Array.isArray(templates) ? templates : []).filter(template => normalizeSearchText([
            template?.name,
            template?.description,
            template?.ownerUsername
        ].join(' ')).includes(normalizedQuery))
    }, [deferredSearchTerm, templates])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(calc(100vw - 40px), 720px)',
                    maxWidth: '720px',
                    borderRadius: '16px',
                    background: 'linear-gradient(180deg, rgba(31,44,70,0.98) 0%, rgba(24,36,59,0.98) 100%)',
                    border: '1px solid rgba(255,255,255,0.08)'
                }
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(4, 10, 20, 0.62)'
                }
            }}
        >
            <DialogTitle sx={{ px: 2.5, pt: 2.2, pb: 1.3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#f3f7ff' }}>
                        Select Attack Template
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: '#a9b4ca' }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pb: 2.5 }}>
                <TextField
                    fullWidth
                    placeholder="Search by name or user"
                    value={searchTerm}
                    onChange={event => setSearchTerm(event.target.value)}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            minHeight: 44,
                            borderRadius: '10px',
                            backgroundColor: 'rgba(17, 26, 44, 0.92)'
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRoundedIcon sx={{ color: '#a9b4ca' }} />
                            </InputAdornment>
                        )
                    }}
                />

                {error && (
                    <Alert
                        severity="error"
                        action={
                            <Button color="inherit" size="small" onClick={onReload}>
                                Retry
                            </Button>
                        }
                        sx={{ mb: 2, borderRadius: 2 }}
                    >
                        Could not load saved attack templates.
                    </Alert>
                )}

                <Box sx={{ maxHeight: 'min(52vh, 520px)', overflowY: 'auto', pr: 0.5 }}>
                    {loading ? (
                        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                            <CircularProgress />
                        </Stack>
                    ) : filteredTemplates.length > 0 ? (
                        <Stack spacing={1.3}>
                            {filteredTemplates.map(template => (
                                <Box
                                    key={template.id}
                                    onClick={() => onSelect(template)}
                                    sx={{
                                        p: 1.7,
                                        borderRadius: '12px',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        backgroundColor: 'rgba(255,255,255,0.03)',
                                        cursor: 'pointer',
                                        transition: 'background-color 160ms ease, border-color 160ms ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                            borderColor: 'rgba(94, 157, 255, 0.36)'
                                        }
                                    }}
                                >
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#f2f6ff' }}>
                                        {template?.name || 'Untitled'}
                                    </Typography>
                                    <Typography sx={{ mt: 0.65, color: '#a8b4cc', fontSize: '0.94rem' }}>
                                        {template?.description || 'No description'}
                                    </Typography>
                                    <Typography sx={{ mt: 1.1, color: '#94a4c3', fontSize: '0.9rem' }}>
                                        {`Saved by ${template?.ownerUsername || 'Unknown'}`}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    ) : (
                        <Box
                            sx={{
                                p: 2.2,
                                borderRadius: '12px',
                                border: '1px solid rgba(255,255,255,0.08)',
                                backgroundColor: 'rgba(255,255,255,0.03)'
                            }}
                        >
                            <Typography sx={{ fontWeight: 700, color: '#f2f6ff' }}>
                                No saved attack templates yet.
                            </Typography>
                            <Typography sx={{ mt: 0.75, color: '#a8b4cc' }}>
                                Create templates in Attack Templates first, then select them here.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    )
}

function TroopPickerDialog({ open, troops, onClose, onSelect }) {
    const [searchTerm, setSearchTerm] = React.useState('')
    const [favoritesOnly, setFavoritesOnly] = React.useState(false)
    const [favoriteIds, setFavoriteIds] = React.useState(loadTroopPickerFavorites)
    const deferredSearchTerm = React.useDeferredValue(searchTerm)

    React.useEffect(() => {
        if (open) {
            setSearchTerm('')
            setFavoritesOnly(false)
        }
    }, [open])

    React.useEffect(() => {
        saveTroopPickerFavorites(favoriteIds)
    }, [favoriteIds])

    const filteredTroops = React.useMemo(() => {
        const normalizedQuery = normalizeSearchText(deferredSearchTerm)
        let nextTroops = Array.isArray(troops) ? troops : []

        if (favoritesOnly)
            nextTroops = nextTroops.filter(troop => favoriteIds.includes(troop.id))

        if (normalizedQuery)
            nextTroops = nextTroops.filter(troop => normalizeSearchText(troop.searchText).includes(normalizedQuery))

        return nextTroops
    }, [deferredSearchTerm, favoriteIds, favoritesOnly, troops])

    const toggleFavorite = troopId => {
        setFavoriteIds(previous =>
            previous.includes(troopId) ?
                previous.filter(currentId => currentId !== troopId) :
                [...previous, troopId]
        )
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(calc(100vw - 40px), 760px)',
                    maxWidth: '760px',
                    borderRadius: '18px',
                    background: 'linear-gradient(180deg, rgba(31,44,70,0.98) 0%, rgba(24,36,59,0.98) 100%)',
                    border: '1px solid rgba(255,255,255,0.08)'
                }
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(4, 10, 20, 0.62)'
                }
            }}
        >
            <DialogTitle sx={{ px: 2.5, pt: 2.2, pb: 1.2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                        <SearchRoundedIcon sx={{ color: '#f3f7ff', fontSize: 28 }} />
                        <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#f3f7ff' }}>
                            Troop Picker
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} sx={{ color: '#a9b4ca' }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pb: 2.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mb: 1.6 }}>
                    <TextField
                        fullWidth
                        placeholder="Search by name or ID..."
                        value={searchTerm}
                        onChange={event => setSearchTerm(event.target.value)}
                        sx={PRIMARY_INPUT_SX}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon sx={{ color: '#a9b4ca' }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        variant="outlined"
                        onClick={() => setFavoritesOnly(previous => !previous)}
                        sx={{
                            minWidth: 116,
                            borderRadius: 2.5,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderColor: favoritesOnly ? 'rgba(67, 128, 255, 0.6)' : 'rgba(129, 146, 178, 0.22)',
                            backgroundColor: favoritesOnly ? 'rgba(67, 128, 255, 0.14)' : 'transparent'
                        }}
                        startIcon={favoritesOnly ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                    >
                        Favorites
                    </Button>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.9, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        startIcon={<FilterAltRoundedIcon />}
                        sx={{
                            borderRadius: 2.5,
                            textTransform: 'none',
                            fontWeight: 800,
                            color: '#f5f7ff',
                            borderColor: 'rgba(67, 128, 255, 0.6)',
                            backgroundColor: 'rgba(67, 128, 255, 0.14)'
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <span>All Troops</span>
                            <Box sx={{ px: 0.9, py: 0.2, borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.08)', fontSize: '0.82rem' }}>
                                {troops.length}
                            </Box>
                        </Stack>
                    </Button>
                </Stack>

                <Typography sx={{ color: '#96a5c0', fontSize: '0.92rem', mb: 1.4 }}>
                    {`${filteredTroops.length} results`}
                </Typography>

                <Box
                    sx={{
                        maxHeight: 'min(58vh, 560px)',
                        overflowY: 'auto',
                        pr: 0.5,
                        borderRadius: '14px',
                        border: '1px solid rgba(129, 146, 178, 0.18)',
                        backgroundColor: 'rgba(16, 25, 41, 0.48)',
                        p: 1.2
                    }}
                >
                    {filteredTroops.length ? (
                        <Stack spacing={1.1}>
                            {filteredTroops.map(troop => (
                                <Box
                                    key={troop.key}
                                    onClick={() => onSelect(troop)}
                                    sx={{
                                        p: 1.6,
                                        borderRadius: '12px',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        backgroundColor: 'rgba(18, 28, 47, 0.76)',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'rgba(28, 42, 67, 0.9)',
                                            borderColor: 'rgba(67, 128, 255, 0.55)'
                                        }
                                    }}
                                >
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                                            <UnitIconFrame
                                                iconUrl={troop.iconUrl}
                                                itemType="troop"
                                                level={resolveUnitLevelBadge(troop)}
                                                size={52}
                                            />
                                            <Stack spacing={1} sx={{ minWidth: 0 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography sx={{ color: '#56a0ff', fontSize: '0.95rem', fontWeight: 700 }}>
                                                    {`#${troop.id}`}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        px: 1,
                                                        py: 0.35,
                                                        borderRadius: 999,
                                                        backgroundColor: 'rgba(84, 100, 131, 0.3)',
                                                        border: '1px solid rgba(147, 162, 188, 0.2)',
                                                        color: '#dce6ff',
                                                        fontSize: '0.82rem',
                                                        fontWeight: 700,
                                                        textTransform: 'capitalize'
                                                    }}
                                                >
                                                    {troop.statLine || 'Troop'}
                                                </Box>
                                            </Stack>
                                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                                                {troop.label.replace(/^[0-9]+:\s*/, '')}
                                            </Typography>
                                            <Typography sx={{ color: '#96a5c0', fontSize: '0.95rem' }}>
                                                {`Level ${troop.level || '0'}`}
                                            </Typography>
                                            </Stack>
                                        </Stack>
                                        <IconButton
                                            onClick={event => {
                                                event.stopPropagation()
                                                toggleFavorite(troop.id)
                                            }}
                                            sx={{ color: favoriteIds.includes(troop.id) ? '#ff6b76' : '#a9b4ca' }}
                                        >
                                            {favoriteIds.includes(troop.id) ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                                        </IconButton>
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    ) : (
                        <Box sx={{ py: 6, textAlign: 'center', color: '#96a5c0', fontSize: '0.98rem' }}>
                            No troops match the current search.
                        </Box>
                    )}
                </Box>

                <Typography sx={{ color: '#7d8aa4', fontSize: '0.88rem', textAlign: 'center', mt: 1.5 }}>
                    Click to select. Use
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            mx: 0.5,
                            color: '#ff5f70',
                            verticalAlign: 'middle'
                        }}
                    >
                        <FavoriteRoundedIcon sx={{ fontSize: 14 }} />
                    </Box>
                    to favorite. Type ID manually in the input field.
                </Typography>
            </DialogContent>
        </Dialog>
    )
}

function TemplateField({ label, helper, value, placeholder, disabled = false, onOpen }) {
    return (
        <Box>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                {label}
            </Typography>
            <Stack direction="row" spacing={1}>
                {(value?.id || value?.label) ? (
                    <UnitIconFrame
                        iconUrl={value?.iconUrl}
                        itemType="troop"
                        level={resolveUnitLevelBadge(value)}
                        size={44}
                    />
                ) : null}
                <TextField
                    fullWidth
                    value={value || ''}
                    placeholder={placeholder}
                    disabled={disabled}
                    InputProps={{ readOnly: true }}
                    sx={PRIMARY_INPUT_SX}
                />
                <IconButton
                    onClick={onOpen}
                    disabled={disabled}
                    sx={{
                        mt: 0.15,
                        width: 44,
                        height: 44,
                        borderRadius: '10px',
                        border: '1px solid rgba(129, 146, 178, 0.22)',
                        color: '#f5f7ff'
                    }}
                >
                    <SearchRoundedIcon />
                </IconButton>
            </Stack>
            <HelperText>{helper}</HelperText>
        </Box>
    )
}

function TroopField({ label, helper, value, placeholder, onOpen, onManualChange, disabled = false }) {
    return (
        <Box>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                {label}
            </Typography>
            <Stack direction="row" spacing={1}>
                <TextField
                    fullWidth
                    value={value?.label || value?.id || ''}
                    placeholder={placeholder}
                    disabled={disabled}
                    onChange={event => onManualChange(event.target.value)}
                    sx={PRIMARY_INPUT_SX}
                />
                <IconButton
                    onClick={onOpen}
                    disabled={disabled}
                    sx={{
                        mt: 0.15,
                        width: 44,
                        height: 44,
                        borderRadius: '10px',
                        border: '1px solid rgba(129, 146, 178, 0.22)',
                        color: '#f5f7ff'
                    }}
                >
                    <SearchRoundedIcon />
                </IconButton>
            </Stack>
            {(value?.id || value?.label) && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <Box
                        sx={{
                            px: 1,
                            py: 0.35,
                            borderRadius: 999,
                            backgroundColor: 'rgba(84, 100, 131, 0.3)',
                            border: '1px solid rgba(147, 162, 188, 0.2)',
                            color: '#dce6ff',
                            fontSize: '0.82rem',
                            fontWeight: 700
                        }}
                    >
                        {value?.statLine || 'Troop'}
                    </Box>
                    {!!value?.level && (
                        <Typography sx={{ color: '#96a5c0', fontSize: '0.92rem' }}>
                            {`Level ${value.level}`}
                        </Typography>
                    )}
                </Stack>
            )}
            <HelperText>{helper}</HelperText>
        </Box>
    )
}

function TargetRuleCard({ rule, onChange }) {
    return (
        <Box
            sx={{ ...TARGET_RULE_CARD_SX, p: 1.6 }}
        >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Stack direction="row" spacing={1.6} alignItems="center" sx={{ minWidth: 220 }}>
                    <Box
                        sx={{
                            px: 1.6,
                            py: 0.95,
                            minWidth: 106,
                            borderRadius: 1.8,
                            border: '1px solid rgba(129, 146, 178, 0.24)',
                            backgroundColor: 'rgba(23, 34, 55, 0.72)',
                            color: '#f5f7ff',
                            fontSize: '0.98rem',
                            fontWeight: 800,
                            textAlign: 'center'
                        }}
                    >
                        {rule.key}
                    </Box>
                </Stack>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ color: '#c8d4ec', fontSize: '0.9rem', mb: 0.55 }}>
                        Setup to use
                    </Typography>
                    <TextField
                        select
                        value={rule.setup}
                        onChange={event => onChange({ ...rule, setup: event.target.value })}
                        sx={{ ...PRIMARY_INPUT_SX, maxWidth: 142 }}
                    >
                        <MenuItem value="EMPTY">EMPTY</MenuItem>
                        <MenuItem value="FULL">FULL</MenuItem>
                    </TextField>
                </Box>

                <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 110, justifyContent: 'flex-end' }}>
                    <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 700 }}>
                        Active
                    </Typography>
                    <SettingSwitch checked={rule.active} onChange={value => onChange({ ...rule, active: value })} />
                </Stack>
            </Stack>
        </Box>
    )
}

function SupplySection({
    title,
    enabledLabel,
    enabledHelper,
    sourceLabel,
    sourceHelper,
    minimumLabel,
    minimumHelper,
    targetLabel,
    targetHelper,
    keepLabel,
    keepHelper,
    sendLabel,
    sendHelper,
    skipTypeLabel,
    skipTypeHelper,
    skipAmountLabel,
    skipAmountHelper,
    value,
    onChange,
    troopField = null,
    extraField = null
}) {
    const updateField = (field, nextValue) => onChange({ ...value, [field]: nextValue })
    const disabled = !value.enabled

    return (
        <Stack spacing={2.2}>
            <SectionHeading>{title}</SectionHeading>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2.4
                }}
            >
                <ToggleField
                    checked={value.enabled}
                    onChange={nextValue => updateField('enabled', nextValue)}
                    label={enabledLabel}
                    helper={enabledHelper}
                />

                <Box>
                    <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                        {sourceLabel}
                    </Typography>
                    <TextField
                        select
                        value={value.sourceCastle}
                        onChange={event => updateField('sourceCastle', event.target.value)}
                        disabled={disabled}
                        sx={{ ...PRIMARY_INPUT_SX, maxWidth: 280 }}
                    >
                        {SOURCE_CASTLE_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <HelperText sx={{ opacity: disabled ? 0.55 : 1 }}>{sourceHelper}</HelperText>
                </Box>

                {troopField ? (
                    <Box sx={{ gridColumn: { xs: '1 / -1', xl: '1 / -1' } }}>
                        {troopField}
                    </Box>
                ) : null}

                <Box>
                    <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                        {minimumLabel}
                    </Typography>
                    <TextField
                        fullWidth
                        value={value.minimumStorm}
                        onChange={event => updateField('minimumStorm', event.target.value)}
                        disabled={disabled}
                        sx={PRIMARY_INPUT_SX}
                    />
                    <HelperText sx={{ opacity: disabled ? 0.55 : 1 }}>{minimumHelper}</HelperText>
                </Box>

                <Box>
                    <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                        {targetLabel}
                    </Typography>
                    <TextField
                        fullWidth
                        value={value.targetStorm}
                        onChange={event => updateField('targetStorm', event.target.value)}
                        disabled={disabled}
                        sx={PRIMARY_INPUT_SX}
                    />
                    <HelperText sx={{ opacity: disabled ? 0.55 : 1 }}>{targetHelper}</HelperText>
                </Box>

                <Box>
                    <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                        {keepLabel}
                    </Typography>
                    <TextField
                        fullWidth
                        value={value.keepMinimumSource}
                        onChange={event => updateField('keepMinimumSource', event.target.value)}
                        disabled={disabled}
                        sx={PRIMARY_INPUT_SX}
                    />
                    <HelperText sx={{ opacity: disabled ? 0.55 : 1 }}>{keepHelper}</HelperText>
                </Box>

                <Box>
                    <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                        {sendLabel}
                    </Typography>
                    <TextField
                        fullWidth
                        value={value.minimumSendAmount}
                        onChange={event => updateField('minimumSendAmount', event.target.value)}
                        disabled={disabled}
                        sx={PRIMARY_INPUT_SX}
                    />
                    <HelperText sx={{ opacity: disabled ? 0.55 : 1 }}>{sendHelper}</HelperText>
                </Box>

                {extraField ? (
                    <Box sx={{ gridColumn: { xs: '1 / -1', xl: '1 / -1' } }}>
                        {extraField}
                    </Box>
                ) : null}

                <Box>
                    <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                        {skipTypeLabel}
                    </Typography>
                    <TextField
                        select
                        value={value.timeSkipType}
                        onChange={event => updateField('timeSkipType', event.target.value)}
                        disabled={disabled}
                        sx={{ ...PRIMARY_INPUT_SX, maxWidth: 270 }}
                    >
                        {SKIP_TIME_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <HelperText sx={{ opacity: disabled ? 0.55 : 1 }}>{skipTypeHelper}</HelperText>
                </Box>

                <Box>
                    <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
                        {skipAmountLabel}
                    </Typography>
                    <TextField
                        fullWidth
                        value={value.timeSkipAmount}
                        onChange={event => updateField('timeSkipAmount', event.target.value)}
                        disabled={disabled}
                        sx={PRIMARY_INPUT_SX}
                    />
                    <HelperText sx={{ opacity: disabled ? 0.55 : 1 }}>{skipAmountHelper}</HelperText>
                </Box>
            </Box>
        </Stack>
    )
}

export default function StormBotSettingsPanel({ userPlugins, onChange, runtimeStatus = null, dailyAttackStatus = null, appLanguage = 'en', templatePlatform = 'gge' }) {
    const activeConfig = React.useMemo(() => normalizeStormBotConfig(userPlugins?.[STORM_PLUGIN_KEY]), [userPlugins])
    const cargoConfig = React.useMemo(() => normalizeStormCargoConfig(userPlugins?.[CARGO_PLUGIN_KEY]), [userPlugins])
    const [templatePickerTarget, setTemplatePickerTarget] = React.useState('')
    const [troopPickerTarget, setTroopPickerTarget] = React.useState('')
    const [templates, setTemplates] = React.useState([])
    const [templatesLoading, setTemplatesLoading] = React.useState(false)
    const [templatesError, setTemplatesError] = React.useState('')
    const [catalog, setCatalog] = React.useState({ attackTroops: [], defenseTroops: [] })

    const troopEntries = React.useMemo(() => buildTroopCatalogEntries(catalog), [catalog])
    const troopEntriesById = React.useMemo(
        () => new Map(troopEntries.map(entry => [entry.id, entry])),
        [troopEntries]
    )
    const resolveDisplayedTroopReference = React.useCallback(
        value => troopEntriesById.get(String(value?.id ?? '').trim()) ?? value,
        [troopEntriesById]
    )

    const commitConfig = React.useCallback(nextConfig => {
        const nextPlugins = cloneValue(userPlugins ?? {})

        nextPlugins[STORM_PLUGIN_KEY] ??= {}
        nextPlugins[STORM_PLUGIN_KEY].state = resolveStormBotRuntimeState(nextPlugins[STORM_PLUGIN_KEY], nextConfig)
        nextPlugins[STORM_PLUGIN_KEY][STORM_SETTINGS_KEY] = nextConfig
        nextPlugins[STORM_PLUGIN_KEY].useLeather = Boolean(nextConfig.useFeathers)
        nextPlugins[STORM_PLUGIN_KEY].useVipCommanders = Boolean(nextConfig.useVipCommanders)
        nextPlugins[STORM_PLUGIN_KEY].buycoins = Boolean(nextConfig.aquaShop.buyCoins)
        nextPlugins[STORM_PLUGIN_KEY].buydeco = Boolean(nextConfig.aquaShop.buyWhaleLagoon)
        nextPlugins[STORM_PLUGIN_KEY].buy5hSkip = Boolean(nextConfig.aquaShop.buy5hSkips)
        nextPlugins[STORM_PLUGIN_KEY].buy24hSkip = Boolean(nextConfig.aquaShop.buy24hSkips)
        nextPlugins[STORM_PLUGIN_KEY].buyFalsePeasants = Boolean(nextConfig.aquaShop.buyDummyPeasants)
        nextPlugins[STORM_PLUGIN_KEY].falsePeasantPackCost = '1570'

        onChange(nextPlugins)
    }, [onChange, userPlugins])

    const commitCargoConfig = React.useCallback(nextCargoConfig => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        const cargoAutomationEnabled = Boolean(nextCargoConfig.shipBuildingEnabled || nextCargoConfig.autoUpgradeStorehouse)
        const skipAutomationEnabled = Boolean(nextCargoConfig.useMinuteSkips && hasSelectedCargoSkipType(nextCargoConfig))

        nextPlugins[CARGO_PLUGIN_KEY] ??= {}
        nextPlugins[CARGO_PLUGIN_KEY][CARGO_SETTINGS_KEY] = nextCargoConfig
        nextPlugins[CARGO_PLUGIN_KEY].enabled = cargoAutomationEnabled
        nextPlugins[CARGO_PLUGIN_KEY].state = resolveStormCargoRuntimeState({
            stormPluginConfig: nextPlugins[STORM_PLUGIN_KEY],
            nextCargoConfig
        })
        nextPlugins[CARGO_PLUGIN_KEY].useSkips = skipAutomationEnabled

        onChange(nextPlugins)
    }, [onChange, userPlugins])

    const updateConfig = React.useCallback(updater => {
        const nextConfig = cloneValue(activeConfig)
        updater(nextConfig)
        commitConfig(nextConfig)
    }, [activeConfig, commitConfig])

    const updateCargoConfig = React.useCallback(updater => {
        const nextCargoConfig = cloneValue(cargoConfig)
        updater(nextCargoConfig)
        commitCargoConfig(nextCargoConfig)
    }, [cargoConfig, commitCargoConfig])

    const updateField = (field, value) => updateConfig(nextConfig => {
        nextConfig[field] = value
    })

    const updateNestedField = (groupKey, field, value) => updateConfig(nextConfig => {
        nextConfig[groupKey][field] = value
    })

    const updateCargoField = (field, value) => updateCargoConfig(nextCargoConfig => {
        nextCargoConfig[field] = value
    })

    const updateSiegeWorkshopField = (field, value) => updateConfig(nextConfig => {
        nextConfig.siegeWorkshop[field] = value
    })

    const updateSiegeWorkshopSlot = (slotIndex, field, value) => updateConfig(nextConfig => {
        nextConfig.siegeWorkshop.slots[slotIndex][field] = value
    })

    const updateCargoLevel = (level, value) => updateCargoConfig(nextCargoConfig => {
        nextCargoConfig.upgradeLevels[level] = value
    })

    const updateCargoSkipType = (skipTypeKey, value) => updateCargoConfig(nextCargoConfig => {
        nextCargoConfig.allowedSkipTypes[skipTypeKey] = value
    })

    const updateTroopReference = (groupKey, troopValue) => updateConfig(nextConfig => {
        nextConfig[groupKey].troop = normalizeTroopReference(troopValue)
    })

    const updateTargetRule = (ruleKey, nextRule) => updateConfig(nextConfig => {
        const ruleIndex = nextConfig.targetRules.findIndex(rule => rule.key === ruleKey)
        if (ruleIndex >= 0)
            nextConfig.targetRules[ruleIndex] = normalizeTargetRule(nextRule, ruleIndex)
    })

    const loadTemplates = React.useCallback(async () => {
        setTemplatesLoading(true)
        setTemplatesError('')

        try {
            const response = await fetch(buildSavedAttackTemplatesUrl(templatePlatform), {
                headers: { Accept: 'application/json' }
            })
            if (!response.ok)
                throw new Error(`HTTP ${response.status}`)

            const payload = await response.json()
            setTemplates(
                [...(Array.isArray(payload?.templates) ? payload.templates : [])].sort((left, right) =>
                    Number(right?.updatedAt ?? 0) - Number(left?.updatedAt ?? 0) ||
                    String(left?.name ?? '').localeCompare(String(right?.name ?? ''))
                )
            )
        }
        catch (error) {
            console.warn('Failed to load storm templates', error)
            setTemplates([])
            setTemplatesError('Could not load saved attack templates.')
        }
        finally {
            setTemplatesLoading(false)
        }
    }, [templatePlatform])

    React.useEffect(() => {
        loadTemplates()
    }, [loadTemplates])

    React.useEffect(() => {
        let cancelled = false

        fetch(`/attack-template-catalog.json?language=${encodeURIComponent(appLanguage || 'en')}`)
            .then(response => {
                if (!response.ok)
                    throw new Error(`HTTP ${response.status}`)
                return response.json()
            })
            .then(payload => {
                if (cancelled)
                    return

                setCatalog({
                    attackTroops: Array.isArray(payload?.attackTroops) ? payload.attackTroops : [],
                    defenseTroops: Array.isArray(payload?.defenseTroops) ? payload.defenseTroops : []
                })
            })
            .catch(error => {
                console.warn('Failed to load storm troop catalog', error)
                if (!cancelled)
                    setCatalog({ attackTroops: [], defenseTroops: [] })
            })

        return () => {
            cancelled = true
        }
    }, [appLanguage])

    const handleTroopSelect = troop => {
        if (!troopPickerTarget || !troop)
            return

        updateTroopReference(troopPickerTarget, troop)
        setTroopPickerTarget('')
    }

    const handleTemplateSelect = template => {
        if (!templatePickerTarget || !template)
            return

        updateConfig(nextConfig => {
            nextConfig.playerGeneratedTemplates.enabled = true
            nextConfig.playerGeneratedTemplates[templatePickerTarget] = normalizeSavedTemplateReference({
                templateId: String(template.id ?? ''),
                name: String(template.name ?? ''),
                ownerUsername: String(template.ownerUsername ?? ''),
                description: String(template.description ?? ''),
                template: cloneValue(template.template ?? {})
            })
        })
        setTemplatePickerTarget('')
    }

    const aquaShopDisabled = !activeConfig.aquaShop.enabled
    const playerTemplatesDisabled = !activeConfig.playerGeneratedTemplates.enabled
    const cargoStatusText = runtimeStatus?.running ? 'Bot is running' : 'Bot is offline'
    const cargoDetailText = runtimeStatus?.running
        ? [
            Number.isFinite(Number(runtimeStatus?.currentShips)) ? `${Number(runtimeStatus.currentShips)} ships` : '',
            Number.isFinite(Number(runtimeStatus?.maxShipLevel)) && Number(runtimeStatus.maxShipLevel) > 0 ? `Max Lv${Number(runtimeStatus.maxShipLevel)}` : '',
            Number.isFinite(Number(runtimeStatus?.storehouseLevel)) && Number(runtimeStatus.storehouseLevel) > 0 ? `Storehouse Lv${Number(runtimeStatus.storehouseLevel)}` : '',
            Number.isFinite(Number(runtimeStatus?.expansionCount)) ? `${Number(runtimeStatus.expansionCount)} expansions` : ''
        ].filter(Boolean).join(' · ') || 'Cargo automation is active'
        : 'Data will appear when bot is running'
    const cargoProgress = runtimeStatus?.running
        ? Math.max(0, Math.min(100, Number(runtimeStatus?.progressPercent ?? (
            !cargoConfig.buildMaxShips && Number(cargoConfig.targetTotalShips) > 0
                ? (Number(runtimeStatus?.currentShips ?? 0) / Number(cargoConfig.targetTotalShips)) * 100
                : 100
        ))))
        : 0
    const vipCommanderUsageCount = Math.max(0, normalizeWholeNumber(dailyAttackStatus?.vipCommanderUsageCount) ?? 0)
    const vipCommanderUsageLimit = Math.max(0, normalizeWholeNumber(dailyAttackStatus?.vipCommanderUsageLimit) ?? 0)
    const vipCommanderUsageRemaining = vipCommanderUsageLimit > 0
        ? Math.max(0, normalizeWholeNumber(dailyAttackStatus?.vipCommanderUsageRemaining) ?? (vipCommanderUsageLimit - vipCommanderUsageCount))
        : undefined
    const vipCommanderLimitReached = Boolean(dailyAttackStatus?.vipCommanderLimitReached)
    const vipCommanderStatusLabel =
        vipCommanderUsageRemaining === undefined ?
            'VIP/daily commander status will appear after the first Storm sync.' :
            `VIP/daily commanders left today: ${vipCommanderUsageRemaining} / ${vipCommanderUsageLimit}`
    const vipCommanderStatusDetail =
        vipCommanderUsageRemaining === undefined ?
            'Data will appear when the bot reports the current daily counter.' :
            `Used today: ${vipCommanderUsageCount}${vipCommanderLimitReached ? ' | Daily VIP limit reached' : ''} | Reset at: ${formatRuntimeDateTime(dailyAttackStatus?.resetAt)}`

    return (
        <Stack spacing={3}>
            <Box sx={{ ...SECTION_SX, p: { xs: 2.2, md: 3 } }}>
                <SectionHeading>Attack Settings</SectionHeading>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.4 }}>
                    <Box>
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>Attack Count Threshold</Typography>
                        <TextField fullWidth value={activeConfig.attackCountThreshold} onChange={event => updateField('attackCountThreshold', event.target.value)} sx={PRIMARY_INPUT_SX} />
                        <HelperText>Number of attacks before stopping (Daily Limit is 3500 after that it will get more expensive)</HelperText>
                    </Box>
                    <Stack spacing={1.45}>
                        <ToggleField checked={activeConfig.useFeathers} onChange={value => updateField('useFeathers', value)} label="Use Feathers" helper="Use feathers for storm attacks" />
                        <ToggleField checked={activeConfig.useVipCommanders} onChange={value => updateField('useVipCommanders', value)} label="Use VIP Commanders" helper="Use VIP or daily commanders for easy/empty storm forts until 300 uses per day, then fall back to the regular Storm Commander Allocation respected from Commander Assignment Editor. Full forts use the same Storm commander allocation selection." />
                        <Box sx={{ ...SUBSECTION_SX, p: 1.55 }}>
                            <Typography sx={{ color: vipCommanderLimitReached ? '#ffb4a2' : '#dce6ff', fontSize: '0.96rem', fontWeight: 800 }}>
                                {vipCommanderStatusLabel}
                            </Typography>
                            <HelperText sx={{ mt: 0.55 }}>
                                {vipCommanderStatusDetail}
                            </HelperText>
                        </Box>
                    </Stack>
                </Box>

                <Box sx={{ mt: 4 }}>
                    <SectionHeading>Ship Status</SectionHeading>
                    <Box sx={{ ...SUBSECTION_SX, p: 2.2 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                            <Typography sx={{ color: runtimeStatus?.running ? '#5be38b' : '#ff8e72', fontSize: '1rem', fontWeight: 800 }}>
                                {cargoStatusText}
                            </Typography>
                            <Typography sx={{ color: 'rgba(205, 216, 237, 0.78)', fontSize: '0.92rem', textAlign: { xs: 'left', md: 'right' } }}>
                                {cargoDetailText}
                            </Typography>
                        </Stack>
                        <Box sx={{ mt: 1.55, height: 8, borderRadius: 999, overflow: 'hidden', backgroundColor: 'rgba(125, 146, 182, 0.16)' }}>
                            <Box
                                sx={{
                                    height: '100%',
                                    width: `${cargoProgress}%`,
                                    borderRadius: 999,
                                    background: runtimeStatus?.running
                                        ? 'linear-gradient(90deg, rgba(59,130,246,0.95) 0%, rgba(91,227,139,0.92) 100%)'
                                        : 'rgba(129, 146, 178, 0.2)',
                                    transition: 'width 180ms ease'
                                }}
                            />
                        </Box>
                        <HelperText sx={{ mt: 1.15 }}>
                            {runtimeStatus?.lastAction || 'Data will appear when bot is running'}
                        </HelperText>
                    </Box>
                </Box>

                <Box sx={{ mt: 4 }}>
                    <SectionHeading>Ships</SectionHeading>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.4 }}>
                        <ToggleField
                            checked={cargoConfig.shipBuildingEnabled}
                            onChange={value => updateCargoField('shipBuildingEnabled', value)}
                            label="Enable ship building"
                            helper="Build and upgrade storm cargo ships automatically."
                        />
                        <ToggleField
                            checked={cargoConfig.buildMaxShips}
                            onChange={value => updateCargoField('buildMaxShips', value)}
                            label="Build max ships"
                            helper="Fill all available storm cargo slots automatically."
                            disabled={!cargoConfig.shipBuildingEnabled}
                        />
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>Target total ships</Typography>
                            <TextField
                                fullWidth
                                value={cargoConfig.targetTotalShips}
                                onChange={event => updateCargoField('targetTotalShips', event.target.value)}
                                sx={PRIMARY_INPUT_SX}
                                disabled={!cargoConfig.shipBuildingEnabled || cargoConfig.buildMaxShips}
                            />
                            <HelperText>Used only when `Build max ships` is disabled.</HelperText>
                        </Box>
                        <Box sx={{ ...SUBSECTION_SX, p: 1.55 }}>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.75 }}>Upgrade ships to level</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                                {CARGO_LEVEL_OPTIONS.map(level => (
                                    <InlineCheckboxOption
                                        key={level}
                                        checked={Boolean(cargoConfig.upgradeLevels[level])}
                                        onChange={value => updateCargoLevel(level, value)}
                                        label={`Lv${level}`}
                                        disabled={!cargoConfig.shipBuildingEnabled}
                                    />
                                ))}
                            </Box>
                            <HelperText>Each checked level allows the next cargo upgrade step up to that level.</HelperText>
                        </Box>
                        <Box sx={{ ...SUBSECTION_SX, p: 1.55 }}>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.75 }}>Construction skips</Typography>
                            <ToggleField
                                checked={cargoConfig.useMinuteSkips}
                                onChange={value => updateCargoField('useMinuteSkips', value)}
                                label="Use construction skips"
                                helper="Use selected time skips on in-progress cargo and storehouse actions."
                                disabled={!cargoConfig.shipBuildingEnabled}
                            />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', mt: 1.1 }}>
                                {CARGO_SKIP_OPTIONS.map(option => (
                                    <InlineCheckboxOption
                                        key={option.key}
                                        checked={Boolean(cargoConfig.allowedSkipTypes[option.key])}
                                        onChange={value => updateCargoSkipType(option.key, value)}
                                        label={option.label}
                                        disabled={!cargoConfig.shipBuildingEnabled || !cargoConfig.useMinuteSkips}
                                    />
                                ))}
                            </Box>
                            <HelperText>Bot tries only the checked skip types, in order from 24h down to 1m.</HelperText>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ mt: 4 }}>
                    <SectionHeading>Storehouse</SectionHeading>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.4 }}>
                        <ToggleField
                            checked={cargoConfig.autoUpgradeStorehouse}
                            onChange={value => updateCargoField('autoUpgradeStorehouse', value)}
                            label="Auto-upgrade Storehouse"
                            helper="Upgrade the storm storehouse with wood/stone up to level 7."
                        />
                    </Box>
                </Box>

                <Box sx={{ mt: 4 }}>
                    <SectionHeading>Castle Expansion</SectionHeading>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.4 }}>
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>Expansion mode</Typography>
                            <TextField
                                select
                                fullWidth
                                value={cargoConfig.expansionMode}
                                onChange={event => updateCargoField('expansionMode', event.target.value)}
                                sx={PRIMARY_INPUT_SX}
                                disabled={!cargoConfig.shipBuildingEnabled}
                            >
                                {EXPANSION_MODE_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <HelperText>Use expansion automatically when new cargo space is needed.</HelperText>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ mt: 4 }}>
                    <SectionHeading>Scan Settings</SectionHeading>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.4 }}>
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>Scan Interval (hours)</Typography>
                            <TextField fullWidth value={activeConfig.scanIntervalHours} onChange={event => updateField('scanIntervalHours', event.target.value)} sx={PRIMARY_INPUT_SX} />
                            <HelperText>Full storm map rescan interval. Use 0.5 for 30 minutes. After each rescan, newly found nearby forts move to the front of the queue. StormBot enforces a 30-minute minimum between full scans.</HelperText>
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>Minimum Wins Remaining</Typography>
                            <TextField fullWidth value={activeConfig.minimumWinsRemaining} onChange={event => updateField('minimumWinsRemaining', event.target.value)} sx={PRIMARY_INPUT_SX} />
                            <HelperText>Minimum wins remaining before stopping</HelperText>
                        </Box>
                        <Box sx={{ gridColumn: { xs: '1 / -1', xl: '1 / -1' }, ...SUBSECTION_SX, p: 2.2 }}>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 1.1 }}>Target Rules + Setup</Typography>
                            <HelperText sx={{ mt: 0, mb: 1.7 }}>
                                Storm attacks the closest active forts first. Use the Active switch to include or skip a target type. EMPTY means the empty-target attack setup, FULL means the full-target attack setup.
                            </HelperText>
                            <Stack spacing={1.3}>
                                {activeConfig.targetRules.map(rule => (
                                    <TargetRuleCard
                                        key={rule.key}
                                        rule={rule}
                                        onChange={nextRule => updateTargetRule(rule.key, nextRule)}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ mt: 4 }}>
                    <SectionHeading>Automatic Setup Settings</SectionHeading>
                    <HelperText sx={{ mt: 0, mb: 1.9 }}>Used by the built-in automatic setup when player-generated templates are disabled.</HelperText>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.4 }}>
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>Support Wave Amount</Typography>
                            <TextField fullWidth value={activeConfig.automaticSetup.supportWaveAmount} onChange={event => updateNestedField('automaticSetup', 'supportWaveAmount', event.target.value)} sx={PRIMARY_INPUT_SX} />
                            <HelperText>Amount of troops in support waves</HelperText>
                        </Box>
                        <TroopField
                            label="Troop ID"
                            helper="Select troop to use for automatic storm setup"
                            value={resolveDisplayedTroopReference(activeConfig.automaticSetup.troop)}
                            placeholder="Select troop to use for automatic storm setup"
                            onOpen={() => setTroopPickerTarget('automaticSetup')}
                            onManualChange={value => updateTroopReference('automaticSetup', { id: value, label: value, statLine: 'Troop', level: '' })}
                        />
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>Wave 3-4 Troop Count</Typography>
                            <TextField fullWidth value={activeConfig.automaticSetup.wave34TroopCount} onChange={event => updateNestedField('automaticSetup', 'wave34TroopCount', event.target.value)} sx={PRIMARY_INPUT_SX} />
                            <HelperText>Number of troops in waves 3-4 for automatic setup</HelperText>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ mt: 4 }}>
                    <SectionHeading>Player-Generated Templates</SectionHeading>
                    <HelperText sx={{ mt: 0, mb: 1.9 }}>Template-based setup configured by the player. Selecting a template here enables template mode automatically.</HelperText>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.4 }}>
                        <ToggleField
                            checked={activeConfig.playerGeneratedTemplates.enabled}
                            onChange={value => updateConfig(nextConfig => {
                                nextConfig.playerGeneratedTemplates.enabled = value
                            })}
                            label="Use Player-Generated Templates"
                            helper="Use selected player-generated templates instead of auto-generated setup."
                        />
                        <TemplateField
                            label="Empty Setup Template"
                            helper="Template used for EMPTY setup targets when template mode is enabled."
                            value={activeConfig.playerGeneratedTemplates.emptyTemplate.name}
                            placeholder="Template used for EMPTY setup targets when template mode is enabled."
                            disabled={playerTemplatesDisabled}
                            onOpen={() => setTemplatePickerTarget('emptyTemplate')}
                        />
                        <TemplateField
                            label="Full Setup Template"
                            helper="Template used for FULL setup targets when template mode is enabled."
                            value={activeConfig.playerGeneratedTemplates.fullTemplate.name}
                            placeholder="Template used for FULL setup targets when template mode is enabled."
                            disabled={playerTemplatesDisabled}
                            onOpen={() => setTemplatePickerTarget('fullTemplate')}
                        />
                    </Box>
                </Box>

                <Box sx={{ mt: 4 }}>
                    <SectionHeading>Tool Settings</SectionHeading>
                    <HelperText sx={{ mt: 0, mb: 1.9 }}>Auto-buy thresholds for Storm tools. The bot buys up to these minimum stock levels.</HelperText>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.4 }}>
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>Minimum Ladders</Typography>
                            <TextField fullWidth value={activeConfig.toolSettings.minimumLadders} onChange={event => updateConfig(nextConfig => { nextConfig.toolSettings.minimumLadders = event.target.value })} sx={PRIMARY_INPUT_SX} />
                            <HelperText>Minimum ladders to keep in stock. If below this value, StormBot auto-buys ladders.</HelperText>
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>Minimum Rams</Typography>
                            <TextField fullWidth value={activeConfig.toolSettings.minimumRams} onChange={event => updateConfig(nextConfig => { nextConfig.toolSettings.minimumRams = event.target.value })} sx={PRIMARY_INPUT_SX} />
                            <HelperText>Minimum rams to keep in stock. If below this value, StormBot auto-buys rams.</HelperText>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ mt: 4 }}>
                    <SectionHeading>Siege Workshop</SectionHeading>
                    <HelperText sx={{ mt: 0, mb: 1.9 }}>
                        Produces only Scaling ladders and Mantlets. StormBot scans castles with a siege workshop, checks free queue slots and resources, then fills available slots.
                    </HelperText>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.4 }}>
                        <ToggleField
                            checked={activeConfig.siegeWorkshop.enabled}
                            onChange={value => updateSiegeWorkshopField('enabled', value)}
                            label="Enable Siege Workshop"
                            helper="Automatically queue selected siege tools when resources and free production slots are available."
                        />
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>Check interval seconds</Typography>
                            <TextField
                                fullWidth
                                value={activeConfig.siegeWorkshop.intervalSeconds}
                                onChange={event => updateSiegeWorkshopField('intervalSeconds', event.target.value)}
                                sx={PRIMARY_INPUT_SX}
                                disabled={!activeConfig.siegeWorkshop.enabled}
                            />
                            <HelperText>Minimum safe scan interval is 30 seconds.</HelperText>
                        </Box>
                    </Box>
                    <Stack spacing={1.25} sx={{ mt: 2 }}>
                        {activeConfig.siegeWorkshop.slots.map((slot, index) => (
                            <Box
                                key={`siege-workshop-slot-${index + 1}`}
                                sx={{
                                    ...SUBSECTION_SX,
                                    p: 1.55,
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 160px' },
                                    gap: 1.35,
                                    alignItems: 'center'
                                }}
                            >
                                <Box>
                                    <Typography sx={{ color: '#f5f7ff', fontSize: '0.94rem', fontWeight: 800, mb: 0.75 }}>
                                        {`Slot ${index + 1} Tool`}
                                    </Typography>
                                    <TextField
                                        select
                                        fullWidth
                                        value={slot.tool}
                                        onChange={event => updateSiegeWorkshopSlot(index, 'tool', event.target.value)}
                                        sx={PRIMARY_INPUT_SX}
                                        disabled={!activeConfig.siegeWorkshop.enabled}
                                    >
                                        {SIEGE_WORKSHOP_TOOL_OPTIONS.map(option => (
                                            <MenuItem key={option.value || 'disabled'} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                                <Box>
                                    <Typography sx={{ color: '#f5f7ff', fontSize: '0.94rem', fontWeight: 800, mb: 0.75 }}>
                                        Amount
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        value={slot.amount}
                                        onChange={event => updateSiegeWorkshopSlot(index, 'amount', event.target.value)}
                                        sx={PRIMARY_INPUT_SX}
                                        disabled={!activeConfig.siegeWorkshop.enabled || !slot.tool}
                                    />
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </Box>

                <Box sx={{ mt: 4 }}>
                    <SectionHeading>Aqua Shop</SectionHeading>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.2 }}>
                        {AQUA_SHOP_FIELDS.map(field => (
                            <ToggleField
                                key={field.key}
                                checked={Boolean(activeConfig.aquaShop[field.key])}
                                onChange={value => updateConfig(nextConfig => { nextConfig.aquaShop[field.key] = value })}
                                label={field.label}
                                helper={field.helper}
                                disabled={field.key !== 'enabled' && aquaShopDisabled}
                                iconUrl={AQUA_SHOP_ICON_BY_KEY[field.key]}
                                iconType={getAquaShopIconType(field.key)}
                            />
                        ))}
                    </Box>
                </Box>

                <Box sx={{ mt: 4 }}>
                    <SupplySection
                        title="Food Supply"
                        enabledLabel="Enable Food Supply"
                        enabledHelper="Enable automatic food supply to storm"
                        sourceLabel="Food: Source Castle"
                        sourceHelper="Source castle for food transport (fallback to Main if selected castle does not exist)"
                        minimumLabel="Food: Minimum Storm"
                        minimumHelper="Minimum food to keep in storm"
                        targetLabel="Food: Target Storm"
                        targetHelper="Target food amount in storm"
                        keepLabel="Food: Keep Minimum Source"
                        keepHelper="Minimum food to keep in the selected source castle"
                        sendLabel="Food: Minimum Send Amount"
                        sendHelper="Minimum amount of food to send"
                        skipTypeLabel="Food: Time Skip Type"
                        skipTypeHelper="Time skip type for food transport"
                        skipAmountLabel="Food: Time Skip Amount"
                        skipAmountHelper="Amount of time skips for food transport"
                        value={activeConfig.foodSupply}
                        onChange={nextValue => updateConfig(nextConfig => { nextConfig.foodSupply = nextValue })}
                    />
                </Box>

                <Box sx={{ mt: 4 }}>
                    <SupplySection
                        title="Mead Supply"
                        enabledLabel="Enable Mead Supply"
                        enabledHelper="Enable automatic mead supply to storm"
                        sourceLabel="Mead: Source Castle"
                        sourceHelper="Source castle for mead transport (fallback to Main if selected castle does not exist)"
                        minimumLabel="Mead: Minimum Storm"
                        minimumHelper="Minimum mead to keep in storm"
                        targetLabel="Mead: Target Storm"
                        targetHelper="Target mead amount in storm"
                        keepLabel="Mead: Keep Minimum Source"
                        keepHelper="Minimum mead to keep in the selected source castle"
                        sendLabel="Mead: Minimum Send Amount"
                        sendHelper="Minimum amount of mead to send"
                        skipTypeLabel="Mead: Time Skip Type"
                        skipTypeHelper="Time skip type for mead transport"
                        skipAmountLabel="Mead: Time Skip Amount"
                        skipAmountHelper="Amount of time skips for mead transport"
                        value={activeConfig.meadSupply}
                        onChange={nextValue => updateConfig(nextConfig => { nextConfig.meadSupply = nextValue })}
                    />
                </Box>

                <Box sx={{ mt: 4 }}>
                    <SupplySection
                        title="Troop Supply"
                        enabledLabel="Enable Troop Supply"
                        enabledHelper="Enable automatic troop supply to storm"
                        sourceLabel="Troops: Source Castle"
                        sourceHelper="Source castle for troop transport (fallback to Main if selected castle does not exist)"
                        minimumLabel="Troops: Minimum Storm"
                        minimumHelper="Minimum troops to keep in storm"
                        targetLabel="Troops: Target Storm"
                        targetHelper="Target troop amount in storm"
                        keepLabel="Troops: Keep Minimum Source"
                        keepHelper="Minimum troops to keep in the selected source castle"
                        sendLabel="Troops: Minimum Send Amount"
                        sendHelper="Minimum amount of troops to send"
                        skipTypeLabel="Troops: Time Skip Type"
                        skipTypeHelper="Time skip type for troop transport"
                        skipAmountLabel="Troops: Time Skip Amount"
                        skipAmountHelper="Amount of time skips for troop transport"
                        value={activeConfig.troopSupply}
                        onChange={nextValue => updateConfig(nextConfig => { nextConfig.troopSupply = nextValue })}
                        troopField={
                            <TroopField
                                label="Troops: Supply Troop Type"
                                helper="Troop type used for Storm troop supply transfers"
                                value={resolveDisplayedTroopReference(activeConfig.troopSupply.troop)}
                                placeholder="Select troop type"
                                onOpen={() => setTroopPickerTarget('troopSupply')}
                                onManualChange={value => updateTroopReference('troopSupply', { id: value, label: value, statLine: 'Troop', level: '' })}
                                disabled={!activeConfig.troopSupply.enabled}
                            />
                        }
                        extraField={
                            <ToggleField
                                checked={activeConfig.troopSupply.requireFoodMead}
                                onChange={value => updateConfig(nextConfig => { nextConfig.troopSupply.requireFoodMead = value })}
                                label="Troops: Require 10k Food/Mead"
                                helper="Do not send troops if Storm has less than 10,000 food or 10,000 mead"
                                disabled={!activeConfig.troopSupply.enabled}
                            />
                        }
                    />
                </Box>
            </Box>

            <TemplatePickerDialog
                open={Boolean(templatePickerTarget)}
                templates={templates}
                loading={templatesLoading}
                error={templatesError}
                onClose={() => setTemplatePickerTarget('')}
                onReload={loadTemplates}
                onSelect={handleTemplateSelect}
            />

            <TroopPickerDialog
                open={Boolean(troopPickerTarget)}
                troops={troopEntries}
                onClose={() => setTroopPickerTarget('')}
                onSelect={handleTroopSelect}
            />
        </Stack>
    )
}
